import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Alert } from 'react-bootstrap';
import { UserDashboardLayout } from '../../layouts/UserDashboardLayout';
import { getAppliedJobs } from '../../services/VacanciesService';
import { LogoSpinner } from '../../components/LogoSpinner';

const formatSalary = (min, max) => {
    if (!min && !max) return 'No especificado';
    if (min && max) {
        return `RD$ ${min.toLocaleString('es-DO')} - RD$ ${max.toLocaleString('es-DO')}`;
    }
    if (min) return `Desde RD$ ${min.toLocaleString('es-DO')}`;
    if (max) return `Hasta RD$ ${max.toLocaleString('es-DO')}`;
    return 'No especificado';
};

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Hoy';
    if (diffDays === 1) return 'Ayer';
    if (diffDays < 7) return `Hace ${diffDays} días`;
    if (diffDays < 30) return `Hace ${Math.floor(diffDays / 7)} semanas`;
    if (diffDays < 365) return `Hace ${Math.floor(diffDays / 30)} meses`;
    return date.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
};

const getStatusBadge = (estado) => {
    const statusConfig = {
        'Borrador': { color: '#6c757d', text: 'Borrador' },
        'Enviado': { color: '#007bff', text: 'Enviado' },
        'En Revisión': { color: '#ffc107', text: 'En Revisión' },
        'Preseleccionado': { color: '#17a2b8', text: 'Preseleccionado ⭐' },
        'Aceptado': { color: '#28a745', text: 'Aceptado' },
        'Rechazado': { color: '#dc3545', text: 'Rechazado' },
    };

    const config = statusConfig[estado] || { color: '#6c757d', text: estado };

    return (
        <span style={{
            backgroundColor: config.color,
            color: '#fff',
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '600'
        }}>
            {config.text}
        </span>
    );
};

export function AppliedJobs() {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
    });

    useEffect(() => {
        loadApplications();
    }, [pagination.page]);

    const loadApplications = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getAppliedJobs({
                page: pagination.page,
                limit: pagination.limit
            });

            setApplications(response.data.data || []);
            setPagination({
                page: response.data.page || 1,
                limit: response.data.limit || 10,
                total: response.data.total || 0,
                totalPages: response.data.totalPages || 0
            });
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Error al cargar las postulaciones';
            setError(errorMessage);
            console.error('Error loading applications:', err);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, page: newPage }));
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    if (loading) {
        return (
            <UserDashboardLayout>
                <LogoSpinner />
            </UserDashboardLayout>
        );
    }

    return (
        <UserDashboardLayout>
            <div className="browse-job">
                <div className="job-bx-title clearfix">
                    <h5 className="font-weight-700 pull-left text-uppercase">
                        {pagination.total} Postulación{pagination.total !== 1 ? 'es' : ''}
                    </h5>
                </div>

                {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible>
                        {error}
                    </Alert>
                )}

                {applications.length === 0 ? (
                    <div className="text-center p-5">
                        <i className="fa fa-briefcase" style={{ fontSize: '64px', color: '#ccc', marginBottom: '20px' }}></i>
                        <h4>No has aplicado a ninguna vacante aún</h4>
                        <p className="text-muted">Explora las vacantes disponibles y comienza a postularte</p>
                        <Link to="/browse-job-filter-grid" className="site-button m-t20">
                            Buscar Vacantes
                        </Link>
                    </div>
                ) : (
                    <>
                        <ul className="post-job-bx browse-job">
                            {applications.map((application) => {
                                const vacancy = application.vacante || {};
                                const company = vacancy.user?.perfilEmpresa || {};
                                const location = `${vacancy.ciudad?.nombre || 'N/A'}, ${vacancy.pais?.nombre || 'N/A'}`;
                                const salary = formatSalary(vacancy.salarioMinimo, vacancy.salarioMaximo);

                                return (
                                    <li key={application.id}>
                                        <div className="post-bx">
                                            <div className="job-post-info m-a0">
                                                <h4>
                                                    <Link to={`/job-detail?id=${vacancy.id}`} style={{ color: '#009247' }}>
                                                        {vacancy.titulo || 'Sin título'}
                                                    </Link>
                                                </h4>
                                                <ul>
                                                    <li>
                                                        <i className="fa fa-building-o m-r5"></i>
                                                        {company.razonSocial || 'Empresa no especificada'}
                                                    </li>
                                                    <li>
                                                        <i className="fa fa-map-marker m-r5"></i>
                                                        {location}
                                                    </li>
                                                    <li>
                                                        <i className="fa fa-money m-r5"></i>
                                                        {salary}
                                                    </li>
                                                    <li>
                                                        <i className="fa fa-calendar m-r5"></i>
                                                        Aplicado: {formatDate(application.createdAt)}
                                                    </li>
                                                </ul>
                                                {vacancy.tags && vacancy.tags.length > 0 && (
                                                    <div className="job-time m-t15 m-b10">
                                                        {vacancy.tags.slice(0, 4).map((tagItem, idx) => (
                                                            <span
                                                                key={idx}
                                                                className="mr-1"
                                                                style={{
                                                                    display: 'inline-block',
                                                                    background: '#f5f5f5',
                                                                    color: '#666',
                                                                    padding: '5px 12px',
                                                                    borderRadius: '3px',
                                                                    fontSize: '12px',
                                                                    marginRight: '5px',
                                                                    marginBottom: '5px'
                                                                }}
                                                            >
                                                                {tagItem.tag?.nombre}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                                <div className="posted-info clearfix">
                                                    <div className="float-left">
                                                        <span className="text-black m-r10">Estado:</span>
                                                        {getStatusBadge(application.estado)}
                                                    </div>
                                                    <div className="float-right">
                                                        {application.estado === 'Borrador' && (
                                                            <Link
                                                                to={`/my-resume?applicationId=${application.id}`}
                                                                className="site-button button-sm m-r5"
                                                            >
                                                                Completar
                                                            </Link>
                                                        )}
                                                        <Link
                                                            to={`/job-detail?id=${vacancy.id}`}
                                                            className="site-button button-sm"
                                                            style={{
                                                                background: '#fff',
                                                                color: '#009247',
                                                                border: '1px solid #009247'
                                                            }}
                                                        >
                                                            Ver Detalle
                                                        </Link>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>

                        {pagination.totalPages > 1 && (
                            <div className="pagination-bx m-t30">
                                <ul className="pagination">
                                    <li className={`previous ${pagination.page === 1 ? 'disabled' : ''}`}>
                                        <Link
                                            to="#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (pagination.page > 1) handlePageChange(pagination.page - 1);
                                            }}
                                        >
                                            <i className="ti-arrow-left"></i> Anterior
                                        </Link>
                                    </li>
                                    {[...Array(pagination.totalPages)].map((_, index) => {
                                        const pageNum = index + 1;
                                        return (
                                            <li key={pageNum} className={pagination.page === pageNum ? 'active' : ''}>
                                                <Link
                                                    to="#"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handlePageChange(pageNum);
                                                    }}
                                                >
                                                    {pageNum}
                                                </Link>
                                            </li>
                                        );
                                    })}
                                    <li className={`next ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                        <Link
                                            to="#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (pagination.page < pagination.totalPages) handlePageChange(pagination.page + 1);
                                            }}
                                        >
                                            Siguiente <i className="ti-arrow-right"></i>
                                        </Link>
                                    </li>
                                </ul>
                            </div>
                        )}
                    </>
                )}
            </div>
        </UserDashboardLayout>
    );
}
